import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, AccordionDetails, Box, Button } from '@mui/material';

import urls from '../../urls';
import LoginModal from '../../components/Login/LoginModal';
import RegisterModal from '../../components/Register/RegisterModal';
import { PortfolioNoticeModal } from '../../components/PortfolioNoticeModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import styles from './LandingPage.module.css';

const LandingPage = () => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userInfo);
  const { isDark, colors } = useThemeStyles();

  const isLocalEnv =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Set CSS custom properties for theme-aware styling
  // Use specific primitive color values instead of the full `colors` object to avoid
  // running on every render (colors is a new object reference each render).
  const textPrimary = colors.text.primary;
  const textSecondary = colors.text.secondary;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--landing-bg-primary', isDark ? '#1a1a2e' : '#ffffff');
    root.style.setProperty('--landing-bg-secondary', isDark ? '#212121' : '#f8f9ff');
    root.style.setProperty('--landing-bg-card', isDark ? '#2a2a3e' : '#f8f9ff');
    root.style.setProperty('--landing-text-primary', isDark ? textPrimary : '#1a1a2e');
    root.style.setProperty('--landing-text-secondary', isDark ? textSecondary : '#6b7280');
    root.style.setProperty('--landing-border', isDark ? '#3a3a4e' : '#e5e7eb');
    root.style.setProperty('--landing-scrollbar-track', isDark ? '#2a2a3e' : '#f1f5f9');
  }, [isDark, textPrimary, textSecondary]);

  // Show portfolio modal on mount unless previously dismissed
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem('plotwyze_portfolio_modal_dismissed') === 'true';
      if (!dismissed) {
        setIsPortfolioModalOpen(true);
      }
    } catch (error) {
      // If localStorage is unavailable, still show the modal
      setIsPortfolioModalOpen(true);
    }
  }, []);

  const handleClosePortfolioModal = () => {
    setIsPortfolioModalOpen(false);
    try {
      localStorage.setItem('plotwyze_portfolio_modal_dismissed', 'true');
    } catch (error) {
      // Ignore persistence errors
    }
  };

  const handleGetStarted = () => {
    if (user?._id) {
      navigate(urls.dashboard);
    } else {
      setIsRegisterOpen(true);
    }
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const features = [
    {
      icon: <DescriptionIcon />,
      title: 'Word Processor',
      description:
        'Powerful rich text editor with formatting, styling, and distraction-free writing.',
    },
    {
      icon: <DashboardIcon />,
      title: 'Corkboard Planning',
      description: 'Visual story planning with interactive corkboards for organizing plot points.',
    },
    {
      icon: <PersonIcon />,
      title: 'Character Notes',
      description: 'Keep detailed character profiles, relationships, and development arcs.',
    },
    {
      icon: <TimelineIcon />,
      title: 'Timeline Notes',
      description: 'Track story events chronologically and maintain narrative consistency.',
    },
    {
      icon: <SentimentSatisfiedIcon />,
      title: 'AI Sentiment Analysis',
      description: 'Analyze emotional tone and pacing throughout your story.',
    },
    {
      icon: <CheckCircleIcon />,
      title: 'AI Continuity Checking',
      description: 'Automatically detect inconsistencies and plot holes in your narrative.',
    },
  ];

  const writerTypes = [
    'Novelists',
    'Screenwriters',
    'Short-Story Writers',
    'Game Writers',
    'Students',
    'Hobbyists',
  ];

  const faqItems = [
    {
      question: 'How does PlotWyze differ from other writing tools?',
      answer:
        'PlotWyze combines word processing, visual planning, and AI-powered analysis in one integrated workspace, designed specifically for creative writers who need both structure and flexibility.',
    },
    {
      question: 'Can I export my work?',
      answer:
        'Yes! You can export your projects to DOCX format, ensuring your work is always accessible and portable.',
    },
    {
      question: 'Is my work stored securely?',
      answer:
        'Absolutely. All your projects are stored securely in the cloud with regular backups. Your privacy and data security are our top priorities.',
    },
    {
      question: 'Does PlotWyze work offline?',
      answer:
        'Currently, PlotWyze requires an internet connection to sync your work. We are working on offline capabilities for future releases.',
    },
    {
      question: 'Can I collaborate with others?',
      answer:
        'Yes! PlotWyze supports collaboration with co-authors and editors. You can share projects and work together in real-time.',
    },
    {
      question: 'What platforms does PlotWyze support?',
      answer:
        'PlotWyze is a web-based application that works on all modern browsers and devices, including Windows, Mac, Linux, tablets, and smartphones.',
    },
  ];

  // Theme-aware styles that maintain the same color scheme
  const themeStyles = {
    landingPage: {
      background: isDark
        ? 'linear-gradient(180deg, #1a1a2e 0%, #212121 100%)'
        : 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)',
      color: isDark ? colors.text.primary : '#1a1a2e',
    },
    hero: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    section: {
      background: isDark ? '#1a1a2e' : 'white',
    },
    sectionAlt: {
      background: isDark ? '#212121' : '#f8f9ff',
    },
    card: {
      background: isDark ? '#2a2a3e' : '#f8f9ff',
      borderColor: isDark ? '#3a3a4e' : '#e5e7eb',
    },
    cardHover: {
      borderColor: '#667eea',
    },
    text: {
      primary: isDark ? colors.text.primary : '#1a1a2e',
      secondary: isDark ? colors.text.secondary : '#6b7280',
    },
    pricingCard: {
      background: isDark ? '#2a2a3e' : '#f8f9ff',
    },
    faqCard: {
      background: isDark ? '#2a2a3e' : 'white',
      borderColor: isDark ? '#3a3a4e' : '#e5e7eb',
    },
  };

  return (
    <div className={styles.landingPage} style={themeStyles.landingPage}>
      {/* Hero Section */}
      <section className={styles.hero} style={themeStyles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroHeadline}>Write Better Stories with Clarity.</h1>
            <p className={styles.heroSubtext}>
              PlotWyze helps writers plan, draft, organize, and analyze their stories — all in one
              distraction-free workspace.
            </p>
            <div className={styles.heroButtons}>
              <button type="button" className={styles.primaryButton} onClick={handleGetStarted}>
                {user?._id ? 'Enter' : 'Start Writing'}
              </button>
              {!user?._id && (
                <button type="button" className={styles.secondaryButton}>
                  <PlayArrowIcon className={styles.buttonIcon} />
                  Watch Demo
                </button>
              )}
            </div>
          </div>
          <div className={styles.heroImage}>
            <div
              className={styles.mockupPlaceholder}
              style={{
                background: isDark ? '#2a2a3e' : 'white',
              }}
            >
              <div className={styles.mockupContent}>
                <div className={styles.mockupHeader}>
                  <div
                    className={styles.mockupDot}
                    style={{
                      background: isDark ? '#4a4a5e' : '#e5e7eb',
                    }}
                  />
                  <div
                    className={styles.mockupDot}
                    style={{
                      background: isDark ? '#4a4a5e' : '#e5e7eb',
                    }}
                  />
                  <div
                    className={styles.mockupDot}
                    style={{
                      background: isDark ? '#4a4a5e' : '#e5e7eb',
                    }}
                  />
                </div>
                <div className={styles.mockupBody}>
                  <div
                    className={styles.mockupLine}
                    style={{
                      background: isDark ? '#4a4a5e' : '#e5e7eb',
                    }}
                  />
                  <div
                    className={styles.mockupLine}
                    style={{
                      background: isDark ? '#4a4a5e' : '#e5e7eb',
                    }}
                  />
                  <div
                    className={styles.mockupLineShort}
                    style={{
                      background: isDark ? '#4a4a5e' : '#e5e7eb',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.features} style={themeStyles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={{ color: themeStyles.text.primary }}>
            Everything You Need to Write
          </h2>
          <div className={styles.featuresGrid}>
            {features.map((feature) => (
              <div
                key={feature.title}
                className={styles.featureCard}
                style={themeStyles.card}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeStyles.cardHover.borderColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = themeStyles.card.borderColor;
                }}
              >
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h3 className={styles.featureTitle} style={{ color: themeStyles.text.primary }}>
                  {feature.title}
                </h3>
                <p
                  className={styles.featureDescription}
                  style={{ color: themeStyles.text.secondary }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.howItWorks} style={themeStyles.sectionAlt}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={{ color: themeStyles.text.primary }}>
            How It Works
          </h2>
          <div className={styles.steps}>
            <div className={styles.step} style={{ background: isDark ? '#2a2a3e' : 'white' }}>
              <div className={styles.stepIcon}>
                <DashboardIcon />
              </div>
              <h3 className={styles.stepTitle} style={{ color: themeStyles.text.primary }}>
                Plan
              </h3>
              <p className={styles.stepDescription} style={{ color: themeStyles.text.secondary }}>
                Organize your story structure with corkboards, character notes, and timelines.
              </p>
            </div>
            <div className={styles.stepArrow}>
              <ArrowForwardIcon />
            </div>
            <div className={styles.step} style={{ background: isDark ? '#2a2a3e' : 'white' }}>
              <div className={styles.stepIcon}>
                <DescriptionIcon />
              </div>
              <h3 className={styles.stepTitle} style={{ color: themeStyles.text.primary }}>
                Write
              </h3>
              <p className={styles.stepDescription} style={{ color: themeStyles.text.secondary }}>
                Draft your story in a distraction-free editor with powerful formatting tools.
              </p>
            </div>
            <div className={styles.stepArrow}>
              <ArrowForwardIcon />
            </div>
            <div className={styles.step} style={{ background: isDark ? '#2a2a3e' : 'white' }}>
              <div className={styles.stepIcon}>
                <CheckCircleIcon />
              </div>
              <h3 className={styles.stepTitle} style={{ color: themeStyles.text.primary }}>
                Refine
              </h3>
              <p className={styles.stepDescription} style={{ color: themeStyles.text.secondary }}>
                Use AI analysis to check continuity, sentiment, and improve your narrative.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interface Showcase */}
      <section className={styles.showcase} style={themeStyles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={{ color: themeStyles.text.primary }}>
            See PlotWyze in Action
          </h2>
          <div className={styles.showcaseScroll}>
            <div className={styles.showcaseItem}>
              <div className={styles.showcasePlaceholder}>
                <DescriptionIcon className={styles.showcaseIcon} />
                <p>Word Processor</p>
              </div>
            </div>
            <div className={styles.showcaseItem}>
              <div className={styles.showcasePlaceholder}>
                <DashboardIcon className={styles.showcaseIcon} />
                <p>Corkboard</p>
              </div>
            </div>
            <div className={styles.showcaseItem}>
              <div className={styles.showcasePlaceholder}>
                <PersonIcon className={styles.showcaseIcon} />
                <p>Character Notes</p>
              </div>
            </div>
            <div className={styles.showcaseItem}>
              <div className={styles.showcasePlaceholder}>
                <TimelineIcon className={styles.showcaseIcon} />
                <p>Timeline View</p>
              </div>
            </div>
            <div className={styles.showcaseItem}>
              <div className={styles.showcasePlaceholder}>
                <SentimentSatisfiedIcon className={styles.showcaseIcon} />
                <p>AI Analysis</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Writer Types Section */}
      <section className={styles.writerTypes} style={themeStyles.sectionAlt}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={{ color: themeStyles.text.primary }}>
            Built for Every Writer
          </h2>
          <div className={styles.writerTypesGrid}>
            {writerTypes.map((type) => (
              <div
                key={type}
                className={styles.writerTypeCard}
                style={{
                  background: isDark ? '#2a2a3e' : 'white',
                  borderColor: themeStyles.card.borderColor,
                  color: themeStyles.text.primary,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.color = '#667eea';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = themeStyles.card.borderColor;
                  e.currentTarget.style.color = themeStyles.text.primary;
                }}
              >
                <p>{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={styles.pricing} style={themeStyles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={{ color: themeStyles.text.primary }}>
            Simple, Transparent Pricing
          </h2>
          <div className={styles.pricingGrid}>
            <div className={styles.pricingCard} style={themeStyles.pricingCard}>
              <h3 className={styles.pricingTitle} style={{ color: themeStyles.text.primary }}>
                Free
              </h3>
              <div className={styles.pricingPrice}>
                <span className={styles.priceAmount} style={{ color: themeStyles.text.primary }}>
                  $0
                </span>
                <span className={styles.pricePeriod} style={{ color: themeStyles.text.secondary }}>
                  /month
                </span>
              </div>
              <ul className={styles.pricingFeatures}>
                <li style={{ color: themeStyles.text.secondary }}>1 Project</li>
                <li style={{ color: themeStyles.text.secondary }}>Basic Editor</li>
                <li style={{ color: themeStyles.text.secondary }}>Limited Notes</li>
                <li style={{ color: themeStyles.text.secondary }}>Community Support</li>
              </ul>
              <button type="button" className={styles.pricingButton}>
                Get Started
              </button>
            </div>
            <div className={`${styles.pricingCard} ${styles.pricingCardFeatured}`}>
              <div className={styles.featuredBadge}>Popular</div>
              <h3 className={styles.pricingTitle}>Pro</h3>
              <div className={styles.pricingPrice}>
                <span className={styles.priceAmount}>$9</span>
                <span className={styles.pricePeriod}>/month</span>
              </div>
              <ul className={styles.pricingFeatures}>
                <li>Unlimited Projects</li>
                <li>Full Editor Features</li>
                <li>Unlimited Notes</li>
                <li>AI Analysis Tools</li>
                <li>Priority Support</li>
              </ul>
              <button
                type="button"
                className={`${styles.pricingButton} ${styles.pricingButtonFeatured}`}
              >
                Start Free Trial
              </button>
            </div>
            <div className={styles.pricingCard} style={themeStyles.pricingCard}>
              <h3 className={styles.pricingTitle} style={{ color: themeStyles.text.primary }}>
                Lifetime
              </h3>
              <div className={styles.pricingPrice}>
                <span className={styles.priceAmount} style={{ color: themeStyles.text.primary }}>
                  $199
                </span>
                <span className={styles.pricePeriod} style={{ color: themeStyles.text.secondary }}>
                  one-time
                </span>
              </div>
              <ul className={styles.pricingFeatures}>
                <li style={{ color: themeStyles.text.secondary }}>Everything in Pro</li>
                <li style={{ color: themeStyles.text.secondary }}>Lifetime Updates</li>
                <li style={{ color: themeStyles.text.secondary }}>Early Access Features</li>
                <li style={{ color: themeStyles.text.secondary }}>Priority Support</li>
                <li style={{ color: themeStyles.text.secondary }}>No Recurring Fees</li>
              </ul>
              <button type="button" className={styles.pricingButton}>
                Buy Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faq} style={themeStyles.sectionAlt}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={{ color: themeStyles.text.primary }}>
            Frequently Asked Questions
          </h2>
          <div className={styles.faqList}>
            {faqItems.map((item, index) => {
              const panelId = `panel${index}`;
              return (
                <Accordion
                  key={item.question}
                  expanded={expanded === panelId}
                  onChange={handleChange(panelId)}
                  className={styles.faqItem}
                  sx={{
                    backgroundColor: themeStyles.faqCard.background,
                    border: `1px solid ${themeStyles.faqCard.borderColor}`,
                    '&:before': {
                      display: 'none',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: themeStyles.text.primary }} />}
                    className={styles.faqQuestion}
                  >
                    <h3 style={{ color: themeStyles.text.primary }}>{item.question}</h3>
                  </AccordionSummary>
                  <AccordionDetails className={styles.faqAnswer}>
                    <p style={{ color: themeStyles.text.secondary }}>{item.answer}</p>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Modal */}
      <PortfolioNoticeModal open={isPortfolioModalOpen} onClose={handleClosePortfolioModal} />

      {/* Local-only helper button to reopen the portfolio modal */}
      {isLocalEnv && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
          }}
        >
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={() => setIsPortfolioModalOpen(true)}
            sx={{
              textTransform: 'none',
              borderRadius: 999,
              fontSize: 12,
              px: 1.75,
              py: 0.5,
              borderColor: colors.border,
              color: colors.text.secondary,
              backgroundColor: isDark ? 'rgba(15,23,42,0.6)' : 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                borderColor: '#667eea',
                color: isDark ? '#e5e7ff' : '#4338ca',
                backgroundColor: isDark ? 'rgba(15,23,42,0.9)' : 'rgba(255,255,255,1)',
              },
              '&:focus-visible': {
                outline: '2px solid #667eea',
                outlineOffset: 2,
              },
            }}
          >
            Reopen portfolio notice
          </Button>
        </Box>
      )}

      {/* Modals */}
      {isLoginOpen && (
        <LoginModal
          onClose={() => setIsLoginOpen(false)}
          switchToRegister={() => {
            setIsRegisterOpen(true);
            setIsLoginOpen(false);
          }}
        />
      )}
      {isRegisterOpen && (
        <RegisterModal
          onClose={() => setIsRegisterOpen(false)}
          switchToLogin={() => {
            setIsRegisterOpen(false);
            setIsLoginOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default LandingPage;
